<template>
  <div class="analytics-container">
    <div class="analytics-header">
      <h2>Wool Supply Chain Analytics</h2>
      <div class="time-filter">
        <label>Time Period:</label>
        <select v-model="timePeriod" @change="updateCharts">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
          <option value="all">All time</option>
        </select>
      </div>
    </div>

    <div class="metrics-dashboard">
      <div class="metric-card">
        <div class="metric-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
            <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
            <line x1="6" y1="1" x2="6" y2="4"></line>
            <line x1="10" y1="1" x2="10" y2="4"></line>
            <line x1="14" y1="1" x2="14" y2="4"></line>
          </svg>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ metrics.totalWoolVolume }}</div>
          <div class="metric-label">Total Wool Volume</div>
          <div class="metric-trend" :class="metrics.volumeTrend > 0 ? 'trend-up' : 'trend-down'">
            <span v-if="metrics.volumeTrend > 0">↑</span>
            <span v-else>↓</span>
            {{ Math.abs(metrics.volumeTrend) }}% from previous period
          </div>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="1" x2="12" y2="23"></line>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ metrics.averagePrice }}</div>
          <div class="metric-label">Average Price per kg</div>
          <div class="metric-trend" :class="metrics.priceTrend > 0 ? 'trend-up' : 'trend-down'">
            <span v-if="metrics.priceTrend > 0">↑</span>
            <span v-else>↓</span>
            {{ Math.abs(metrics.priceTrend) }}% from previous period
          </div>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ metrics.activeFarms }}</div>
          <div class="metric-label">Active Farms</div>
          <div class="metric-trend" :class="metrics.farmsTrend > 0 ? 'trend-up' : 'trend-down'">
            <span v-if="metrics.farmsTrend > 0">↑</span>
            <span v-else>↓</span>
            {{ Math.abs(metrics.farmsTrend) }}% from previous period
          </div>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
          </svg>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ metrics.exportVolume }}</div>
          <div class="metric-label">Export Volume</div>
          <div class="metric-trend" :class="metrics.exportTrend > 0 ? 'trend-up' : 'trend-down'">
            <span v-if="metrics.exportTrend > 0">↑</span>
            <span v-else>↓</span>
            {{ Math.abs(metrics.exportTrend) }}% from previous period
          </div>
        </div>
      </div>
    </div>

    <div class="charts-container">
      <div class="chart-card">
        <h3>Wool Production by Month</h3>
        <div class="chart-container" ref="productionChart"></div>
      </div>
      
      <div class="chart-card">
        <h3>Wool Quality Distribution</h3>
        <div class="chart-container" ref="qualityChart"></div>
      </div>
      
      <div class="chart-card">
        <h3>Top Export Destinations</h3>
        <div class="chart-container" ref="exportChart"></div>
      </div>
      
      <div class="chart-card">
        <h3>Supply Chain Timeline</h3>
        <div class="chart-container" ref="timelineChart"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  props: {
    searchResult: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      timePeriod: '30',
      metrics: {
        totalWoolVolume: '0 kg',
        volumeTrend: 0,
        averagePrice: '$0.00',
        priceTrend: 0,
        activeFarms: 0,
        farmsTrend: 0,
        exportVolume: '0 kg',
        exportTrend: 0
      },
      charts: {
        production: null,
        quality: null,
        export: null,
        timeline: null
      },
      chartData: {
        production: [],
        quality: [],
        export: [],
        timeline: []
      }
    };
  },
  watch: {
    searchResult: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.results) {
          this.generateMetrics();
          this.generateChartData();
        }
      }
    }
  },
  mounted() {
    this.initCharts();
  },
  methods: {
    generateMetrics() {
      // In a real application, these metrics would be calculated from actual data
      // Here we're generating sample metrics based on the search results
      
      if (this.searchResult && this.searchResult.results && this.searchResult.results.length > 0) {
        const result = this.searchResult.results[0];
        
        // Generate sample metrics
        this.metrics = {
          totalWoolVolume: this.formatWeight(Math.random() * 10000 + 5000),
          volumeTrend: Math.round((Math.random() * 20) - 10),
          averagePrice: this.formatPrice(Math.random() * 10 + 5),
          priceTrend: Math.round((Math.random() * 15) - 5),
          activeFarms: Math.round(Math.random() * 100 + 50),
          farmsTrend: Math.round((Math.random() * 10) - 3),
          exportVolume: this.formatWeight(Math.random() * 8000 + 3000),
          exportTrend: Math.round((Math.random() * 25) - 10)
        };
      }
    },
    
    generateChartData() {
      // Generate sample data for charts
      this.generateProductionData();
      this.generateQualityData();
      this.generateExportData();
      this.generateTimelineData();
      
      // Update charts with new data
      this.updateCharts();
    },
    
    generateProductionData() {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      this.chartData.production = months.map(month => ({
        month,
        value: Math.round(Math.random() * 1000 + 500)
      }));
    },
    
    generateQualityData() {
      const qualities = ['Superfine', 'Fine', 'Medium', 'Strong', 'Coarse'];
      this.chartData.quality = qualities.map(quality => ({
        quality,
        value: Math.round(Math.random() * 100)
      }));
    },
    
    generateExportData() {
      const destinations = ['China', 'Italy', 'India', 'Korea', 'Japan', 'UK', 'Germany'];
      this.chartData.export = destinations.map(country => ({
        country,
        value: Math.round(Math.random() * 1000 + 200)
      })).sort((a, b) => b.value - a.value).slice(0, 5);
    },
    
    generateTimelineData() {
      const stages = ['Farm', 'Testing', 'Processing', 'Export', 'Manufacturing'];
      let currentDate = new Date();
      
      this.chartData.timeline = stages.map((stage, index) => {
        const startDate = new Date(currentDate);
        startDate.setDate(startDate.getDate() - (stages.length - index) * 15);
        
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 10);
        
        return {
          stage,
          start: startDate,
          end: endDate
        };
      });
    },
    
    initCharts() {
      this.$nextTick(() => {
        this.initProductionChart();
        this.initQualityChart();
        this.initExportChart();
        this.initTimelineChart();
      });
    },
    
    initProductionChart() {
      const container = this.$refs.productionChart;
      if (!container) return;
      
      // Clear previous chart if exists
      d3.select(container).selectAll('*').remove();
      
      const margin = { top: 20, right: 30, bottom: 40, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = container.clientHeight - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', container.clientWidth)
        .attr('height', container.clientHeight)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Save chart reference for updates
      this.charts.production = {
        svg,
        width,
        height,
        margin
      };
    },
    
    initQualityChart() {
      const container = this.$refs.qualityChart;
      if (!container) return;
      
      // Clear previous chart if exists
      d3.select(container).selectAll('*').remove();
      
      const margin = { top: 20, right: 30, bottom: 40, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = container.clientHeight - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', container.clientWidth)
        .attr('height', container.clientHeight)
        .append('g')
        .attr('transform', `translate(${width / 2 + margin.left},${height / 2 + margin.top})`);
      
      // Save chart reference for updates
      this.charts.quality = {
        svg,
        width,
        height,
        radius: Math.min(width, height) / 2,
        margin
      };
    },
    
    initExportChart() {
      const container = this.$refs.exportChart;
      if (!container) return;
      
      // Clear previous chart if exists
      d3.select(container).selectAll('*').remove();
      
      const margin = { top: 20, right: 30, bottom: 40, left: 100 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = container.clientHeight - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', container.clientWidth)
        .attr('height', container.clientHeight)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Save chart reference for updates
      this.charts.export = {
        svg,
        width,
        height,
        margin
      };
    },
    
    initTimelineChart() {
      const container = this.$refs.timelineChart;
      if (!container) return;
      
      // Clear previous chart if exists
      d3.select(container).selectAll('*').remove();
      
      const margin = { top: 20, right: 30, bottom: 40, left: 100 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = container.clientHeight - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', container.clientWidth)
        .attr('height', container.clientHeight)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Save chart reference for updates
      this.charts.timeline = {
        svg,
        width,
        height,
        margin
      };
    },
    
    updateCharts() {
      this.updateProductionChart();
      this.updateQualityChart();
      this.updateExportChart();
      this.updateTimelineChart();
    },
    
    updateProductionChart() {
      const chart = this.charts.production;
      if (!chart || !this.chartData.production.length) return;
      
      const { svg, width, height } = chart;
      
      // Clear previous chart content
      svg.selectAll('*').remove();
      
      // X scale
      const x = d3.scaleBand()
        .domain(this.chartData.production.map(d => d.month))
        .range([0, width])
        .padding(0.2);
      
      // Y scale
      const y = d3.scaleLinear()
        .domain([0, d3.max(this.chartData.production, d => d.value) * 1.1])
        .range([height, 0]);
      
      // Add X axis
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x))
        .selectAll('text')
        .style('text-anchor', 'middle');
      
      // Add Y axis
      svg.append('g')
        .call(d3.axisLeft(y));
      
      // Add Y axis label
      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('y', -40)
        .attr('x', -height / 2)
        .attr('text-anchor', 'middle')
        .text('Production (kg)');
      
      // Add bars
      svg.selectAll('rect')
        .data(this.chartData.production)
        .enter()
        .append('rect')
        .attr('x', d => x(d.month))
        .attr('y', d => y(d.value))
        .attr('width', x.bandwidth())
        .attr('height', d => height - y(d.value))
        .attr('fill', '#f97316');
    },
    
    updateQualityChart() {
      const chart = this.charts.quality;
      if (!chart || !this.chartData.quality.length) return;
      
      const { svg, radius } = chart;
      
      // Clear previous chart content
      svg.selectAll('*').remove();
      
      // Compute the position of each group on the pie
      const pie = d3.pie()
        .value(d => d.value);
      
      const data_ready = pie(this.chartData.quality);
      
      // Build the pie chart
      const arcGenerator = d3.arc()
        .innerRadius(radius * 0.4)
        .outerRadius(radius * 0.8);
      
      // Color scale
      const color = d3.scaleOrdinal()
        .domain(this.chartData.quality.map(d => d.quality))
        .range(d3.schemeCategory10);
      
      // Add the arcs
      svg.selectAll('slices')
        .data(data_ready)
        .enter()
        .append('path')
        .attr('d', arcGenerator)
        .attr('fill', d => color(d.data.quality))
        .attr('stroke', 'white')
        .style('stroke-width', '2px');
      
      // Add labels
      svg.selectAll('labels')
        .data(data_ready)
        .enter()
        .append('text')
        .text(d => `${d.data.quality} (${d.data.value}%)`)
        .attr('transform', d => {
          const pos = arcGenerator.centroid(d);
          const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
          pos[0] = radius * 0.95 * (midAngle < Math.PI ? 1 : -1);
          return `translate(${pos})`;
        })
        .style('text-anchor', d => {
          const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
          return midAngle < Math.PI ? 'start' : 'end';
        })
        .style('font-size', '12px');
      
      // Add lines connecting labels to arcs
      svg.selectAll('lines')
        .data(data_ready)
        .enter()
        .append('polyline')
        .attr('points', d => {
          const posA = arcGenerator.centroid(d);
          const posB = arcGenerator.centroid(d);
          const posC = arcGenerator.centroid(d);
          const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
          posB[0] = radius * 0.85 * (midAngle < Math.PI ? 1 : -1);
          posC[0] = radius * 0.95 * (midAngle < Math.PI ? 1 : -1);
          return [posA, posB, posC];
        })
        .style('fill', 'none')
        .style('stroke', 'gray')
        .style('stroke-width', '1px');
    },
    
    updateExportChart() {
      const chart = this.charts.export;
      if (!chart || !this.chartData.export.length) return;
      
      const { svg, width, height } = chart;
      
      // Clear previous chart content
      svg.selectAll('*').remove();
      
      // Sort data by value
      const sortedData = [...this.chartData.export].sort((a, b) => a.value - b.value);
      
      // X scale
      const x = d3.scaleLinear()
        .domain([0, d3.max(sortedData, d => d.value) * 1.1])
        .range([0, width]);
      
      // Y scale
      const y = d3.scaleBand()
        .domain(sortedData.map(d => d.country))
        .range([0, height])
        .padding(0.2);
      
      // Add X axis
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x))
        .selectAll('text')
        .style('text-anchor', 'middle');
      
      // Add X axis label
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height + 35)
        .attr('text-anchor', 'middle')
        .text('Volume (kg)');
      
      // Add Y axis
      svg.append('g')
        .call(d3.axisLeft(y));
      
      // Add bars
      svg.selectAll('rect')
        .data(sortedData)
        .enter()
        .append('rect')
        .attr('x', 0)
        .attr('y', d => y(d.country))
        .attr('width', d => x(d.value))
        .attr('height', y.bandwidth())
        .attr('fill', '#3b82f6');
      
      // Add value labels
      svg.selectAll('text.value')
        .data(sortedData)
        .enter()
        .append('text')
        .attr('class', 'value')
        .attr('x', d => x(d.value) + 5)
        .attr('y', d => y(d.country) + y.bandwidth() / 2)
        .attr('dy', '0.35em')
        .text(d => this.formatWeight(d.value))
        .style('font-size', '12px');
    },
    
    updateTimelineChart() {
      const chart = this.charts.timeline;
      if (!chart || !this.chartData.timeline.length) return;
      
      const { svg, width, height } = chart;
      
      // Clear previous chart content
      svg.selectAll('*').remove();
      
      // Get date range
      const minDate = d3.min(this.chartData.timeline, d => d.start);
      const maxDate = d3.max(this.chartData.timeline, d => d.end);
      
      // X scale (time)
      const x = d3.scaleTime()
        .domain([minDate, maxDate])
        .range([0, width]);
      
      // Y scale (stages)
      const y = d3.scaleBand()
        .domain(this.chartData.timeline.map(d => d.stage))
        .range([0, height])
        .padding(0.2);
      
      // Add X axis
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(5).tickFormat(d3.timeFormat('%b %d')));
      
      // Add Y axis
      svg.append('g')
        .call(d3.axisLeft(y));
      
      // Add timeline bars
      svg.selectAll('rect')
        .data(this.chartData.timeline)
        .enter()
        .append('rect')
        .attr('x', d => x(d.start))
        .attr('y', d => y(d.stage))
        .attr('width', d => x(d.end) - x(d.start))
        .attr('height', y.bandwidth())
        .attr('fill', '#8b5cf6')
        .attr('rx', 6)
        .attr('ry', 6);
      
      // Add date labels
      svg.selectAll('text.date')
        .data(this.chartData.timeline)
        .enter()
        .append('text')
        .attr('class', 'date')
        .attr('x', d => x(d.start) + (x(d.end) - x(d.start)) / 2)
        .attr('y', d => y(d.stage) + y.bandwidth() / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .text(d => `${d3.timeFormat('%b %d')(d.start)} - ${d3.timeFormat('%b %d')(d.end)}`)
        .style('font-size', '11px')
        .style('fill', 'white');
    },
    
    formatWeight(value) {
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)} tonnes`;
      }
      return `${Math.round(value)} kg`;
    },
    
    formatPrice(value) {
      return `$${value.toFixed(2)}`;
    }
  }
};
</script>

<style scoped>
.analytics-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  gap: 24px;
}

.analytics-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.analytics-header h2 {
  margin: 0;
  color: #111827;
  font-size: 20px;
}

.time-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}

.time-filter label {
  font-weight: 500;
  color: #4b5563;
}

.time-filter select {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.metrics-dashboard {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.metric-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  margin-right: 16px;
  color: white;
}

.metric-card:nth-child(1) .metric-icon {
  background-color: #f97316;
}

.metric-card:nth-child(2) .metric-icon {
  background-color: #10b981;
}

.metric-card:nth-child(3) .metric-icon {
  background-color: #3b82f6;
}

.metric-card:nth-child(4) .metric-icon {
  background-color: #8b5cf6;
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
}

.metric-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
}

.metric-trend {
  font-size: 12px;
  font-weight: 500;
}

.trend-up {
  color: #10b981;
}

.trend-down {
  color: #ef4444;
}

.charts-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 300px);
  gap: 24px;
}

.chart-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.chart-card h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #111827;
}

.chart-container {
  flex: 1;
  position: relative;
}

@media (max-width: 1024px) {
  .charts-container {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(4, 300px);
  }
}

@media (max-width: 768px) {
  .metrics-dashboard {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .metrics-dashboard {
    grid-template-columns: 1fr;
  }
  
  .analytics-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>
